import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './context/AuthContext';

import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import VoucherCreate from './pages/VoucherCreate';
import VoucherInquiry from './pages/VoucherInquiry';
import VoucherPayment from './pages/VoucherPayment';
import VoucherManagement from './pages/VoucherManagement';
import VoucherDetail from './pages/VoucherDetail';
import PartnerManagement from './pages/PartnerManagement';
import PartnerDetail from './pages/PartnerDetail';
import EditPartner from './pages/EditPartner';
import DeliveryPreferences from './pages/DeliveryPreferences';
import AccountManagement from './pages/AccountManagement';
import AccountDetail from './pages/AccountDetail';
import ControlAccounts from './pages/ControlAccounts';
import FloatManagement from './pages/FloatManagement';
import TransactionManagement from './pages/TransactionManagement';
import TransactionDetail from './pages/TransactionDetail';
import SettlementManagement from './pages/SettlementManagement';
import TariffManagement from './pages/TariffManagement';
import ApprovalQueue from './pages/ApprovalQueue';
import AuditTrail from './pages/AuditTrail';
import Reports from './pages/Reports';
import UserManagement from './pages/UserManagement';
import UserProfile from './pages/UserProfile';
import ProtectedRoute from './components/ProtectedRoute';

const queryClient = new QueryClient();

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Simple root redirect component. There is no app login page: unauthenticated
// visitors are redirected to the Keycloak-hosted login by the auth init.
const RootRedirect: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading || !isAuthenticated) {
    return null; // Keycloak redirect is in flight
  }

  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <Routes>
              {/* Protected routes with Layout */}
              <Route path="/*" element={
                <Layout>
                  <Routes>
                    <Route path="/" element={<RootRedirect />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/voucher/create"
                  element={
                    <ProtectedRoute roles={['MERCHANT']}>
                      <VoucherCreate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/voucher/inquiry"
                  element={
                    <ProtectedRoute roles={['RETAILER', 'MERCHANT', 'ADMIN']}>
                      <VoucherInquiry />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/voucher/payment"
                  element={
                    <ProtectedRoute roles={['RETAILER']}>
                      <VoucherPayment />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/voucher/management"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'OPERATIONS']}>
                      <VoucherManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/voucher/:voucherNumber"
                  element={
                    <ProtectedRoute roles={['RETAILER', 'MERCHANT', 'ADMIN', 'OPERATIONS']}>
                      <VoucherDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/partners"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'OPERATIONS']}>
                      <PartnerManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/partners/:id"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'OPERATIONS']}>
                      <PartnerDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/partners/:id/edit"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'OPERATIONS']}>
                      <EditPartner />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/partners/:id/delivery-preferences"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'OPERATIONS']}>
                      <DeliveryPreferences />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounts"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'FINANCE', 'OPERATIONS']}>
                      <AccountManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounts/control"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'FINANCE']}>
                      <ControlAccounts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounts/:accountNumber"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'FINANCE', 'OPERATIONS']}>
                      <AccountDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounts/float"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'FINANCE', 'OPERATIONS']}>
                      <FloatManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/transactions"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'FINANCE', 'OPERATIONS']}>
                      <TransactionManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/transactions/:id"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'FINANCE', 'OPERATIONS']}>
                      <TransactionDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settlements"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'FINANCE', 'OPERATIONS']}>
                      <SettlementManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tariffs"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'FINANCE']}>
                      <TariffManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/approvals"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'FINANCE', 'OPERATIONS']}>
                      <ApprovalQueue />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/audit"
                  element={
                    <ProtectedRoute roles={['ADMIN', 'COMPLIANCE']}>
                      <AuditTrail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute roles={['MERCHANT', 'ADMIN', 'FINANCE']}>
                      <Reports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute roles={['ADMIN']}>
                      <UserManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <UserProfile />
                    </ProtectedRoute>
                  }
                />
                  </Routes>
                </Layout>
              } />
            </Routes>
          </Router>
        </ThemeProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}

export default App;
